// Carries a finished chatbot conversation over to the Kentico support form.
//
// The form can live on the same page as the chat (revealed in place) or on a different page
// (DocsBot navigates to it), so the conversation is stashed in web storage and picked up again
// wherever the transcript form control happens to render.
//
// localStorage rather than sessionStorage: DocsBot opens its support link in a NEW TAB by
// default, and a tab opened that way starts with an empty sessionStorage (implicit noopener),
// which would lose the conversation. localStorage is shared across tabs of the same origin.
// The short expiry below and clearing on submit keep a stale conversation from attaching
// itself to an unrelated form later on.
import { formatHistory } from './docsbot';
const STORAGE_KEY = 'kcom.chatConversation';
/** Conversations older than this are ignored, so a stale chat never attaches to a later form. */
const MAX_AGE_MS = 2 * 60 * 60 * 1000;
/** Keep the stored text within reach of the Kentico field it is saved into. */
const MAX_LENGTH = 60000;
export const TRANSCRIPT_FIELD_SELECTOR = '.js-chat-transcript';
/** Wrapper added by FormWidgetMarkupInjection, so the whole field can be shown or hidden. */
const TRANSCRIPT_WRAPPER_SELECTOR = '.chat-transcript-field';
export function buildConversation(history, metadata, ticket) {
    return {
        savedAt: Date.now(),
        conversationId: metadata?.conversationId,
        conversationUrl: metadata?.conversationUrl,
        ticketSubject: ticket?.subject,
        ticketMessage: ticket?.message,
        // Which page the visitor was chatting on, which the ticket otherwise would not say.
        websiteUrl: window.location.href,
        transcript: formatHistory(history),
    };
}
/**
 * Renders the conversation as the plain text that gets saved in Kentico. Sections without
 * data are left out - conversationId/conversationUrl only arrive in Agent mode and the
 * ticket only on plans that generate one.
 */
export function formatConversation(conversation) {
    const blocks = [];
    const header = [];
    if (conversation.conversationId) {
        header.push(`Conversation ID: ${conversation.conversationId}`);
    }
    if (conversation.conversationUrl) {
        header.push(`Conversation URL: ${conversation.conversationUrl}`);
    }
    if (conversation.websiteUrl) {
        header.push(`Website URL: ${conversation.websiteUrl}`);
    }
    if (conversation.fromUrlParameters) {
        header.push('Source: URL parameters, not verified against the chat');
    }
    if (header.length > 0) {
        blocks.push(header.join('\n'));
    }
    if (conversation.ticketSubject || conversation.ticketMessage) {
        const summary = ['--- AI summary ---'];
        if (conversation.ticketSubject) {
            summary.push(`Subject: ${conversation.ticketSubject}`);
        }
        if (conversation.ticketMessage) {
            summary.push(conversation.ticketMessage);
        }
        blocks.push(summary.join('\n'));
    }
    if (conversation.transcript) {
        blocks.push(`--- Transcript ---\n${conversation.transcript}`);
    }
    const text = blocks.join('\n\n');
    return text.length > MAX_LENGTH ? `${text.slice(0, MAX_LENGTH)}\n[truncated]` : text;
}
export function saveConversation(conversation) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(conversation));
    }
    catch {
        // Storage can be unavailable (private mode, quota). The same-page flow still works
        // because the transcript is written straight into the field as well.
    }
}
export function readConversation() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
            return null;
        }
        const conversation = JSON.parse(stored);
        if (!conversation?.savedAt || Date.now() - conversation.savedAt > MAX_AGE_MS) {
            clearConversation();
            return null;
        }
        return conversation;
    }
    catch {
        return null;
    }
}
export function clearConversation() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    }
    catch {
        // Nothing to do - see saveConversation.
    }
}
/**
 * Writes the conversation into every transcript control on the page. Returns true when at
 * least one field was filled.
 */
export function writeConversationToForm(conversation) {
    const fields = document.querySelectorAll(TRANSCRIPT_FIELD_SELECTOR);
    if (fields.length === 0) {
        return false;
    }
    const text = formatConversation(conversation);
    fields.forEach((field) => {
        field.value = text;
        // Kentico's form scripts and any validation listen for these, so a value assigned from
        // script has to announce itself.
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: true }));
    });
    // Revealed here rather than by the callers, so that every route that fills the field also shows
    // it. The field starts out hidden on a page load without a conversation, and the hand-off can
    // happen at any point afterwards.
    setTranscriptFieldVisible(true);
    return true;
}
/**
 * Shows or hides the whole transcript field, wrapper and label included.
 *
 * The same support form is also used on its own, reached without ever opening the chat. There is
 * nothing to attach then, and an empty read-only field the visitor cannot do anything with is only
 * confusing - so it stays out of the way until there is a conversation for it.
 */
export function setTranscriptFieldVisible(visible) {
    document.querySelectorAll(TRANSCRIPT_FIELD_SELECTOR).forEach((field) => {
        const wrapper = field.closest(TRANSCRIPT_WRAPPER_SELECTOR);
        if (wrapper) {
            wrapper.hidden = !visible;
        }
    });
}
